import { KPIDrivers, Page } from '@services/api/deckManagement/graphql/schema/API';
import { initializeMetadata } from '@utils/deckManagementEntityHelpers';
import { MenuOption } from 'types/Common.types';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { ContextMenuEnums } from 'types/Page.types';
import { PageModifiableProperties } from 'types/PageContext.types';
import { PageSidePanelDropDown } from 'types/PageSidePanel.types';

/**
 * Menu items used when there are no rows in the AGGrid component.
 * Used when there are no decklines to display (i.e.: new page).
 */
const NO_ROWS_MENU_ITEMS = [{ value: ContextMenuEnums.INSERT_ROW, label: ContextMenuEnums.INSERT_ROW }];

/**
 * Handles generating the label for the comments context menu option
 * @param comments
 * @returns string
 */
export const contextMenuCommentsLabel = (comments: EnhancedDeckline['comments']) => {
  const commentLabelParts = [ContextMenuEnums.COMMENT as string];
  if (comments && comments?.length > 0) {
    commentLabelParts.push(`(${comments.length})`);
  }
  return commentLabelParts.join(' ');
};

/**
 * Handles generating the context menu options for a deckline
 * @param isFirstRowAdd - Whether the row is the first row in the AGGrid component
 * @param selectedDeckline - The selected deckline (if any, otherwise null or undefined)
 * @param isEditingDisabled - Whether editing is disabled due to deck lock
 * @returns MenuOption<ContextMenuEnums>[]
 */
export const pageDecklineContextMenuOptions = (
  isFirstRowAdd: boolean,
  selectedDeckline?: EnhancedDeckline | null,
  isEditingDisabled?: boolean,
): MenuOption<ContextMenuEnums>[] => {
  if (isFirstRowAdd) {
    return NO_ROWS_MENU_ITEMS.map((item) => ({
      ...item,
      disabled: isEditingDisabled,
    }));
  }

  const decklineTypeOptions: MenuOption<ContextMenuEnums>[] = [];
  const isBaseKPI = selectedDeckline?.deckline_type === PageSidePanelDropDown.BASE_KPI;

  // Additional Options specific to the BASE_KPI type:
  if (isBaseKPI) {
    decklineTypeOptions.push({
      value: ContextMenuEnums.DISPLAY_NAME_OVERRIDE,
      label: ContextMenuEnums.DISPLAY_NAME_OVERRIDE,
      disabled: isEditingDisabled,
    });
  }

  return [
    { value: ContextMenuEnums.INSERT_ROW_ABOVE, label: ContextMenuEnums.INSERT_ROW_ABOVE, disabled: isEditingDisabled },
    {
      value: ContextMenuEnums.INSERT_ROW_BELOW,
      label: ContextMenuEnums.INSERT_ROW_BELOW,
      divider: true,
      disabled: isEditingDisabled,
    },
    { value: ContextMenuEnums.FOOTNOTES, label: ContextMenuEnums.FOOTNOTES },
    { value: ContextMenuEnums.COMMENT, label: contextMenuCommentsLabel(selectedDeckline?.comments) },
    { value: ContextMenuEnums.DECKLINE_HISTORY, label: ContextMenuEnums.DECKLINE_HISTORY },
    ...decklineTypeOptions,
    { value: ContextMenuEnums.DELETE_DECKLINE, label: ContextMenuEnums.DELETE_DECKLINE, disabled: isEditingDisabled },
    { value: ContextMenuEnums.DEBUG_OUTPUT_DECKLINE, label: ContextMenuEnums.DEBUG_OUTPUT_DECKLINE },
  ];
};

/**
 * Initializes a Page object with only properties which are editable on the Page View.
 * @param page
 */
export const initializePageModifiableProperties = (page: Page): PageModifiableProperties => {
  const emptyDrivers: KPIDrivers = { __typename: 'KPIDrivers' };
  const modifiable = {
    page_drivers: { ...(page.page_drivers || emptyDrivers) },
    denominator_label: page.denominator_label ?? null,
  };

  return initializeMetadata(modifiable);
};
